// frontend/src/services/games/gameStatsService.ts
//
// Paylaşılan Oyun İstatistikleri servisi. Şu an için localStorage'da tutulur;
// ileride backend senkronizasyonu eklendiğinde bu dosyanın içi değiştirilebilir,
// çağıran kodun (oyun sayfaları / useGameSessionTracking) değişmesi gerekmez.

export interface GameStats {
  gamesPlayed: number;
  highScore: number;
  lastPlayedAt: string | null; // ISO tarih
  totalPlayTimeMs: number;
  /** Oyuna özel, isimlendirilmiş toplayıcı sayaçlar (örn. fruitsEaten, linesCleared, wins). */
  cumulative: Record<string, number>;
}

const STORAGE_KEY = 'minikcoder:game-stats:v1';

function emptyStats(): GameStats {
  return { gamesPlayed: 0, highScore: 0, lastPlayedAt: null, totalPlayTimeMs: 0, cumulative: {} };
}

function readAll(): Record<string, GameStats> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeAll(data: Record<string, GameStats>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // localStorage dolu veya erişilemez olabilir — sessizce yoksay, oyun akışını bozma.
  }
}

export function getGameStats(gameId: string): GameStats {
  return readAll()[gameId] ?? emptyStats();
}

export function getAllGameStats(): Record<string, GameStats> {
  return readAll();
}

export interface RecordSessionInput {
  /** Bu round'da elde edilen skor (varsa). highScore ile karşılaştırılır. */
  score?: number;
  /** Bu round'da geçen süre (ms). totalPlayTimeMs'e eklenir. */
  playTimeMs: number;
  /** Toplayıcı sayaçlara eklenecek delta'lar (örn. { fruitsEaten: 3 }). */
  cumulativeDeltas?: Record<string, number>;
}

export function recordGameSession(gameId: string, input: RecordSessionInput): GameStats {
  const all = readAll();
  const current = all[gameId] ?? emptyStats();

  const nextCumulative = { ...current.cumulative };
  if (input.cumulativeDeltas) {
    for (const [key, delta] of Object.entries(input.cumulativeDeltas)) {
      nextCumulative[key] = (nextCumulative[key] ?? 0) + delta;
    }
  }

  const next: GameStats = {
    gamesPlayed: current.gamesPlayed + 1,
    highScore: Math.max(current.highScore, input.score ?? 0),
    lastPlayedAt: new Date().toISOString(),
    totalPlayTimeMs: current.totalPlayTimeMs + Math.max(0, Math.round(input.playTimeMs)),
    cumulative: nextCumulative,
  };

  all[gameId] = next;
  writeAll(all);
  return next;
}
